import { Injectable } from '@angular/core';
import type {
  ApplicationInsights,
  IExceptionTelemetry,
} from '@microsoft/applicationinsights-web';

import { environment } from '../../../environments/environment';

// SeverityLevel.Error — inlined so the enum isn't pulled into the main bundle.
const SEVERITY_ERROR = 3;

/**
 * Thin wrapper around the Application Insights browser SDK.
 *
 * Initialised once at app bootstrap by an APP_INITIALIZER (see app.module.ts).
 * A no-op when no connection string is configured (local dev) or the CI token
 * was never substituted — so the SPA boots cleanly without telemetry locally.
 *
 * The SDK is loaded via a dynamic import so its ~110 kB stays out of the
 * initial bundle (kept under the 1 MB CI budget). The APP_INITIALIZER awaits
 * init(), so telemetry is live before the first view renders.
 *
 * Once active the SDK auto-captures:
 *   - unhandled window errors + promise rejections
 *   - page views on every router navigation (enableAutoRouteTracking)
 *   - outbound XHR/fetch as dependencies, including failures
 * with W3C trace-context headers so calls correlate with the pipeline's
 * server-side traces in the same App Insights resource.
 *
 * Use `trackException` for errors you catch and want recorded with context
 * (the global ErrorHandler routes uncaught Angular errors here).
 */
@Injectable({ providedIn: 'root' })
export class TelemetryService {
  private appInsights?: ApplicationInsights;

  get enabled(): boolean {
    return !!this.appInsights;
  }

  /** Called by APP_INITIALIZER. Never throws — telemetry must not block boot. */
  async init(): Promise<void> {
    const connectionString = environment.appInsights?.connectionString?.trim();

    // Skip when unset (local dev) or the CI #{...}# token was never replaced.
    if (!connectionString || connectionString.startsWith('#{')) {
      return;
    }

    try {
      const apiHost = new URL(environment.apis.default.url).host;
      const { ApplicationInsights, DistributedTracingModes } = await import(
        '@microsoft/applicationinsights-web'
      );

      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          enableAutoRouteTracking: true,
          enableUnhandledPromiseRejectionTracking: true,
          autoTrackPageVisitTime: true,
          // Correlate browser telemetry with the pipeline's server traces.
          distributedTracingMode: DistributedTracingModes.W3C,
          enableCorsCorrelation: true,
          correlationHeaderDomains: [apiHost],
          loggingLevelConsole: 0,
        },
      });
      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView();
    } catch (err) {
      // Never let telemetry init break the app.
      // eslint-disable-next-line no-console
      console.warn('[Telemetry] App Insights init failed; telemetry disabled.', err);
      this.appInsights = undefined;
    }
  }

  /** Record a handled or uncaught error with optional context properties. */
  trackException(error: unknown, properties?: Record<string, unknown>): void {
    if (!this.appInsights) {
      return;
    }
    const exception = error instanceof Error ? error : new Error(String(error));
    const telemetry: IExceptionTelemetry = {
      exception,
      severityLevel: SEVERITY_ERROR,
    };
    this.appInsights.trackException(telemetry, properties as Record<string, string> | undefined);
  }

  /** Record a custom event (e.g. a domain action worth tracing). */
  trackEvent(name: string, properties?: Record<string, unknown>): void {
    this.appInsights?.trackEvent({ name }, properties as Record<string, string> | undefined);
  }

  /** Tag subsequent telemetry with the signed-in user for diagnosis. */
  setAuthenticatedUser(userId: string, tenant?: string): void {
    this.appInsights?.setAuthenticatedUserContext(userId, tenant, true);
  }

  /** Clear the user context on logout. */
  clearAuthenticatedUser(): void {
    this.appInsights?.clearAuthenticatedUserContext();
  }
}

/** Factory used by APP_INITIALIZER. */
export function telemetryInitializerFactory(svc: TelemetryService): () => Promise<void> {
  return () => svc.init();
}
